/**
 * useChatExport.ts - Export the current chat as a document or as audio.
 *
 * Both paths end at a signed storage URL. The chat SPA runs as a cross-site iframe
 * authenticating with a bearer token from localStorage, so a link back to /ua/... would
 * be fetched without credentials — a signed URL needs none.
 *
 * Documents come back in one response. Audio may not: synthesising a long conversation
 * outruns proxy gateway timeouts, so the server can answer with a jobId to poll.
 */

import { useRef, useState } from 'react'
import { useApi } from 'core'
import { useTranslation } from 'react-i18next'

export type ChatExportFormat = 'docx' | 'markdown'

/** Circle TTS settings, as the per-message player uses (contexts/ChatContext). */
export interface ChatTTSConfig {
    provider?: string
    model: string
    voice: string
    language?: string
    format?: string
}

const POLL_INTERVAL_MS = 2000
const POLL_TIMEOUT_MS = 5 * 60 * 1000

export interface ChatExportResult {
    url: string
    filename: string
}

export const useChatExport = () => {
    const api = useApi()
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [result, setResult] = useState<ChatExportResult | null>(null)

    // Identifies the export currently owning the state. Audio can poll for minutes, and
    // reset() (dialog closed) or a second export must not have that stale run land its
    // result on top afterwards. Bumped by both, checked before every state write.
    const runIdRef = useRef(0)

    // Shared by both paths so the export always carries the labels the user is reading.
    const labels = () => ({
        question: t('export_question', 'Question'),
        answer: t('export_answer', 'Answer'),
        sources: t('sources', 'Sources'),
        createdBy: t('export_created_by', 'Created by'),
        user: t('export_creator_user', 'User'),
        ai: t('export_creator_ai', 'AI assistant'),
        untitledSource: t('export_untitled_source', 'Untitled source'),
        // Sits between a book source's title and its page number: "<book> - page 3".
        page: t('page', 'page'),
    })

    // Polls through the api client rather than raw fetch: this SPA authenticates with a
    // bearer token, which the client attaches and a bare cookie-based fetch would not.
    const pollAudioJob = async (jobId: string, runId: number): Promise<any> => {
        const start = Date.now()
        while (Date.now() - start < POLL_TIMEOUT_MS) {
            await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS))
            // Abandoned while we slept — stop polling rather than finish work nobody
            // is waiting for. null, not a throw: a cancellation is not an error to show.
            if (runIdRef.current !== runId) return null
            const body = await api.get({ path: `tts/status/${jobId}` })
            if (body.status === 'success') return body
            if (body.status === 'failed') throw new Error(body.error || 'Audio generation failed')
        }
        throw new Error('Audio generation timed out')
    }

    const exportChat = async (postId: string, format: ChatExportFormat): Promise<ChatExportResult | null> => {
        const runId = ++runIdRef.current
        setLoading(true)
        setResult(null)
        try {
            const res = await api.post({
                path: 'chat/export',
                body: {
                    postId,
                    format,
                    // The document's headings come from the UI's own translations, so the
                    // export matches the language the user is reading in and the server
                    // needs no parallel translation table.
                    labels: labels(),
                },
            })
            if (runIdRef.current !== runId) {
                return null
            }
            const next = { url: res.url, filename: res.filename }
            setResult(next)
            return next
        } finally {
            if (runIdRef.current === runId) {
                setLoading(false)
            }
        }
    }

    /**
     * Narrate the whole conversation. Sources are excluded server-side — a spoken list
     * of titles and bylines after every answer is unlistenable.
     */
    const exportAudio = async (postId: string, ttsConfig: ChatTTSConfig): Promise<ChatExportResult | null> => {
        const runId = ++runIdRef.current
        setLoading(true)
        setResult(null)
        try {
            let res = await api.post({
                path: 'chat/audio',
                body: {
                    postId,
                    labels: labels(),
                    provider: ttsConfig.provider,
                    model: ttsConfig.model,
                    voice: ttsConfig.voice,
                    language: ttsConfig.language,
                    // No format: narration is always mp3 server-side. A whole
                    // conversation is split across provider requests and rejoined, and
                    // only CBR mp3 survives that concatenation intact.
                },
            })

            // The filename is only on the first response; polling returns the TTS envelope.
            const filename = res.filename
            if (res.status === 'pending' || res.status === 'processing') {
                res = await pollAudioJob(res.jobId, runId)
                if (!res) {
                    return null
                }
            }

            const url = res.data && res.data.audioUrl
            if (!url) {
                throw new Error(res.error || 'Audio generation failed')
            }

            if (runIdRef.current !== runId) {
                return null
            }
            const next = { url, filename: filename || 'chat.mp3' }
            setResult(next)
            return next
        } finally {
            if (runIdRef.current === runId) {
                setLoading(false)
            }
        }
    }

    // Also abandons any export still in flight, so a slow audio job cannot resurface as
    // a "ready" file after the user dismissed it.
    const reset = () => {
        runIdRef.current++
        setResult(null)
        setLoading(false)
    }

    return { loading, result, exportChat, exportAudio, reset }
}
